import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, timeout } from 'rxjs';

import { environment } from '../../../environments/environment';

export interface PublicConfig {
  mfaEnabled: boolean;
  /**
   * The public half of this deployment's reCAPTCHA pair, or empty when it has
   * none. Empty means the script is never loaded and no token is attached —
   * which is also what makes local development work untouched.
   */
  recaptchaSiteKey: string;
}

/**
 * Fails safe: a feature we could not confirm is treated as absent. Showing a
 * setup screen for something the server does not have is worse than omitting
 * a control the user can reach again after a reload.
 */
const DEFAULT_CONFIG: PublicConfig = {
  mfaEnabled: false,
  recaptchaSiteKey: '',
};

/** Bounded so a slow or hung backend cannot wedge app boot. */
const LOAD_TIMEOUT_MS = 5000;

/**
 * What this deployment can do, fetched once before the first route renders.
 *
 * The alternative — inferring a switched-off feature from the 404s its own
 * endpoints return — cannot work on a route reached before sign-in, where
 * "the feature is off" and "you are not signed in" are indistinguishable.
 * Asking once, up front, gives route guards a definite answer.
 */
@Injectable({ providedIn: 'root' })
export class AppConfigService {
  private config: PublicConfig = { ...DEFAULT_CONFIG };

  constructor(private http: HttpClient) {}

  async load(): Promise<void> {
    const baseUrl = environment.ssoBaseUrl.replace(/\/$/, '');
    try {
      const res = await firstValueFrom(
        this.http
          .get<Partial<PublicConfig>>(`${baseUrl}/sso/public-config`)
          .pipe(timeout(LOAD_TIMEOUT_MS)),
      );
      this.config = {
        mfaEnabled: !!res?.mfaEnabled,
        recaptchaSiteKey: (res?.recaptchaSiteKey ?? '').trim(),
      };
    } catch {
      // An older backend has no such route, and an unreachable one answers
      // nothing. Both mean we cannot claim the feature exists.
      this.config = { ...DEFAULT_CONFIG };
    }
  }

  get mfaEnabled(): boolean {
    return this.config.mfaEnabled;
  }

  get recaptchaSiteKey(): string {
    return this.config.recaptchaSiteKey;
  }
}
